// components/CategoryShowcase.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Home, Palette, Coffee, Loader, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Category metadata - warm image cards with gradient fallback
const categoryMeta = {
  'Textiles & Clothing': {
    icon: <ShoppingBag className="w-7 h-7" />,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=700&q=80',
    gradient: 'from-clay-700/85 to-saffron-600/85',
  },
  'Home Decor': {
    icon: <Home className="w-7 h-7" />,
    image: 'https://images.unsplash.com/photo-1610701596007-11502861dcfa?auto=format&fit=crop&w=700&q=80',
    gradient: 'from-cocoa-800/85 to-saffron-700/85',
  },
  'Art & Craft': {
    icon: <Palette className="w-7 h-7" />,
    image: 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?auto=format&fit=crop&w=700&q=80',
    gradient: 'from-saffron-600/85 to-rose-600/85',
  },
  'Food & Sweets': {
    icon: <Coffee className="w-7 h-7" />,
    image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=700&q=80',
    gradient: 'from-amber-700/85 to-rose-700/85',
  },
};

// Default fallback for unknown categories
const defaultMeta = {
  icon: <ShoppingBag className="w-7 h-7" />,
  image: null,
  gradient: 'from-clay-600/85 to-saffron-600/85',
};

const CategoryShowcase = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/products/categories`);

        // Filter out "All" category since we want to display individual categories
        const filteredCategories = response.data.data.filter(cat => cat.name !== 'All');
        setCategories(filteredCategories);
        setError(null);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please try again later.');

        // Fallback to hardcoded categories if API fails
        setCategories([
          { name: 'Textiles & Clothing', count: 20, avgPrice: 0, totalStock: 0 },
          { name: 'Home Decor', count: 15, avgPrice: 0, totalStock: 0 },
          { name: 'Art & Craft', count: 10, avgPrice: 0, totalStock: 0 },
          { name: 'Food & Sweets', count: 5, avgPrice: 0, totalStock: 0 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Get description for category
  const getCategoryDescription = (categoryName) => {
    const descriptions = {
      'Textiles & Clothing': 'Traditional Bengali sarees, fabrics, and garments',
      'Home Decor': 'Handcrafted pottery, wall art, and home accessories',
      'Art & Craft': 'Traditional paintings, sculptures, and crafts',
      'Food & Sweets': 'Authentic Bengali sweets and traditional food items',
    };
    return descriptions[categoryName] || 'Explore our collection';
  };

  // Format category name for URL
  const formatCategoryUrl = (categoryName) => {
    return categoryName
      .toLowerCase()
      .replace(/ & /g, '-and-')
      .replace(/ & /g, '-')
      .replace(/ /g, '-');
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader className="w-8 h-8 animate-spin text-clay-500" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-cream-100 text-clay-600 rounded-full text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            Explore Categories
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-cocoa-900">
            Discover Our Curated Collection
          </h2>
          <p className="text-cocoa-600 max-w-2xl mx-auto text-lg">
            Authentic Bengali products, each carrying a story of heritage and skill
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => (
            <CategoryCard
              key={category.name}
              category={category}
              meta={categoryMeta[category.name] || defaultMeta}
              description={getCategoryDescription(category.name)}
              url={formatCategoryUrl(category.name)}
              index={index}
            />
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/shop"
            className="group inline-flex items-center border-2 border-clay-500 text-clay-600 px-8 py-3 rounded-full font-semibold hover:bg-clay-50 transition-colors"
          >
            View All Categories
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {error && (
          <div className="mt-4 text-center">
            <p className="text-red-600 text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-clay-600 text-sm hover:underline mt-2"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const CategoryCard = ({ category, meta, description, url, index }) => {
  const [imageError, setImageError] = useState(false);
  const showImage = meta.image && !imageError;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.08 }}
    >
      <Link
        to={`/shop?category=${url}`}
        className="group block relative h-80 rounded-3xl overflow-hidden shadow-lg hover:shadow-warm-lg transition-shadow duration-300"
      >
        {/* Image */}
        {showImage ? (
          <img
            src={meta.image}
            alt={category.name}
            onError={() => setImageError(true)}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${meta.gradient}`}></div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-cocoa-950/85 via-cocoa-950/30 to-transparent"></div>

        {/* Content */}
        <div className="absolute inset-0 p-6 flex flex-col justify-between">
          {/* Top Row - Icon + Count */}
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 bg-white/15 backdrop-blur-md text-saffron-200 border border-white/25 rounded-2xl flex items-center justify-center">
              {meta.icon}
            </div>
            <span className="bg-white/15 backdrop-blur-md border border-white/25 text-cream-50 text-xs font-semibold px-3 py-1.5 rounded-full">
              {category.count}+ items
            </span>
          </div>

          {/* Bottom Content */}
          <div>
            <h3 className="text-xl font-bold text-white mb-1.5 group-hover:text-saffron-200 transition-colors">
              {category.name}
            </h3>
            <p className="text-cream-100/85 text-sm mb-3">{description}</p>
            <span className="inline-flex items-center text-saffron-200 font-semibold text-sm">
              Browse Items
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default CategoryShowcase;
